import { BarChart3, UserCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PopoverErrorBoundary } from "@/components/error-boundary/popover-error-boundary";
import { SettingCard } from "@/components/settings/setting-card";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { AppointmentAssignmentToggle } from "./components/appointment-assignment-toggle";
import { OutcomeTrackingConfig } from "./components/outcome-tracking-config";
import { useSalonConfig } from "./hooks/use-salon-config";

export function SalonConfigSettingsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("settings");
  const { form, isLoading, saving, error, save, discard, saveField, savingFields } =
    useSalonConfig();

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={form.formState.isDirty}
      onSave={save}
      onDiscard={discard}
      saving={saving}
      description={t("messages.unsavedChanges")}
    >
      <BaseNavigationBar
        title={t("sections.salonConfig.title")}
        subtitle={t("sections.salonConfig.description")}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <FormActionButtons
            key="form-actions"
            isDirty={form.formState.isDirty}
            onSave={save}
            onDiscard={discard}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <PopoverErrorBoundary>
          <Form {...form}>
            <div className="space-y-6">
              <ErrorAlert error={error} />

              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-48 w-full" />
                </div>
              ) : !error ? (
                <div className="space-y-6">
                  <SettingCard
                    title={t("salonConfig.workerAssignmentTitle")}
                    icon={<UserCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
                  >
                    <AppointmentAssignmentToggle
                      saveField={saveField}
                      saving={savingFields.has("appointmentWorkerAssignmentEnabled")}
                    />
                  </SettingCard>

                  <SettingCard
                    title={t("salonConfig.appointmentOutcomesTitle")}
                    icon={<BarChart3 className="h-5 w-5 text-green-600 dark:text-green-400" />}
                  >
                    <OutcomeTrackingConfig
                      saveField={saveField}
                      saving={savingFields.has("outcomeTrackingEnabled")}
                    />
                  </SettingCard>
                </div>
              ) : null}
            </div>
          </Form>
        </PopoverErrorBoundary>
      </PageContent>
    </NavigationGuardProvider>
  );
}
